import { Button, Space, Tag, Avatar, Typography } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import React from "react";
import { useTableUser } from "../hooks/useTableUser";
import { IUserResFind } from "../../../redux/@types";
import Table from "../../../shared/components/ui/Table";
import { storesService, usersService } from "../../../shared/services";
import { TableColumns } from "../../../shared/components/ui/Table/types";
import { useUsers } from "../hooks/useUsers";

const { Text } = Typography;

const sortQuery = { $sort: { id: -1 } };

export type UsersTableVariant = "system" | "clients" | "domiciliarios";

const roleQueryForVariant = (variant: UsersTableVariant) => {
  if (variant === "clients") return { role: "user" as const };
  if (variant === "domiciliarios") return { role: "domiciliary" as const };
  return { role: { $in: ["admin", "seller"] as const } };
};

export const TableUsers = ({ variant }: { variant: UsersTableVariant }) => {
  const { showDeleteConfirm, changeUserForUpdate } = useTableUser();
  const { setTableProps } = useUsers();
  const [storeLabelById, setStoreLabelById] = React.useState<
    Record<number, string>
  >({});

  React.useEffect(() => {
    if (variant !== "domiciliarios") return;
    storesService
      .find({ query: { $limit: 500, $sort: { name: 1 } } })
      .then((res) => {
        const rows = "data" in res ? res.data : [];
        const list = Array.isArray(rows) ? rows : [];
        const map: Record<number, string> = {};
        for (const s of list as { id: number; name: string; address?: string }[]) {
          const addr = s.address?.trim();
          map[s.id] = addr ? `${s.name} — ${addr}` : s.name;
        }
        setStoreLabelById(map);
      })
      .catch(() => setStoreLabelById({}));
  }, [variant]);

  const getRoleColor = (role: string) => {
    const roleLower = role?.toLowerCase() || "";
    if (roleLower.includes("admin")) return "red";
    if (roleLower === "user") return "blue";
    if (roleLower.includes("cliente") || roleLower.includes("customer")) return "blue";
    if (roleLower.includes("empleado") || roleLower.includes("employee")) return "green";
    return "default";
  };

  const columns: TableColumns<IUserResFind> = [
    {
      title: "Usuario",
      key: "user_info",
      width: 16,
      render: (_, item) => (
        <Space size={6}>
          <Avatar
            size={32}
            style={{
              background: "#E95735",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {item.first_name?.charAt(0)}{item.last_name?.charAt(0)}
          </Avatar>
          <div style={{ minWidth: 0, overflow: "hidden" }}>
            <Text
              strong
              style={{ display: "block", fontSize: 13 }}
              ellipsis={{ tooltip: `${item.first_name} ${item.last_name}` }}
            >
              {item.first_name} {item.last_name}
            </Text>
            <Text
              type="secondary"
              style={{ fontSize: 11 }}
              ellipsis={{ tooltip: item.email }}
            >
              {item.email}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "DNI",
      dataIndex: "dni",
      key: "dni_info",
      width: 7,
      render: (_, item) => (
        <Text style={{ fontSize: 12 }}>
          {item.dni}
        </Text>
      ),
      filterType: "text",
    },
    {
      title: "Teléfono",
      dataIndex: "phone",
      key: "phone",
      width: 8,
      render: (phone: string) => (
        <Text style={{ fontSize: 12 }}>
          {phone || "N/A"}
        </Text>
      ),
      filterType: "text",
    },
    ...(variant === "domiciliarios"
      ? [
          {
            title: "Sede",
            dataIndex: "store_id",
            key: "store_id",
            width: 7,
            render: (_: unknown, item: IUserResFind) => {
              const id = item.store_id;
              if (id == null) {
                return (
                  <Text style={{ fontSize: 12 }} type="secondary">
                    —
                  </Text>
                );
              }
              const label = storeLabelById[id];
              const display = label ?? `#${id}`;
              return (
                <Text
                  style={{ fontSize: 12, maxWidth: 200 }}
                  ellipsis={{ tooltip: display }}
                >
                  {display}
                </Text>
              );
            },
          } as TableColumns<IUserResFind>[number],
        ]
      : []),
    {
      title: "Rol",
      dataIndex: "role",
      key: "role",
      width: 7,
      render: (_, item) => {
        const roleText = 
          item.role === "user" ? "Usuario" :
          item.role === "admin" ? "Admin" :
          item.role === "seller" ? "Vendedor" :
          item.role === "domiciliary" ? "Domiciliario" :
          item.role;
        
        return (
          <Tag
            color={getRoleColor(item.role)}
            style={{
              borderRadius: 6,
              padding: "2px 8px",
              fontSize: 11,
              fontWeight: 500,
              border: "none",
            }}
          >
            {variant === "clients"
              ? "Cliente"
              : variant === "domiciliarios"
                ? "Domiciliario"
                : roleText}
          </Tag>
        );
      },
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      width: 8,
      render: (_, item) => {
        const statusText = 
          item.status === "active" ? "Activo" :
          item.status === "inactive" ? "Inactivo" :
          item.status === "pending security verification" ? "Verif. pend." :
          item.status === "pending user data" ? "Datos pend." :
          item.status === "pending validation" ? "Valid. pend." :
          item.status;
        
        const statusColor = 
          item.status === "active" ? "green" :
          item.status === "inactive" ? "red" :
          "orange";
        
        return (
          <Tag
            color={statusColor}
            style={{
              borderRadius: 6,
              padding: "2px 8px",
              fontSize: 11,
              fontWeight: 500,
              border: "none",
            }}
          >
            {statusText}
          </Tag>
        );
      },
      filters: [
        { value: "active", text: "Activo" },
        { value: "inactive", text: "Inactivo" },
        {
          value: "pending security verification",
          text: "Verificación pendiente",
        },
        { value: "pending user data", text: "Datos pendientes" },
        { value: "pending validation", text: "Validación pendiente" },
      ],
    },
    {
      title: "Acciones",
      key: "operation",
      width: 10,
      render: (_, item) => {
        return (
          <Space size={4}>
            <Button
              type="primary"
              onClick={() => changeUserForUpdate(item)}
              icon={<EditOutlined />}
              size="small"
              style={{
                borderRadius: 6,
                fontWeight: 500,
                fontSize: 12,
              }}
            >
              Editar
            </Button>
            <Button
              danger
              onClick={() =>
                showDeleteConfirm(
                  item.id,
                  `${item.first_name} ${item.last_name}`
                )
              }
              icon={<DeleteOutlined />}
              size="small"
              style={{
                borderRadius: 6,
                fontWeight: 500,
              }}
            />
          </Space>
        );
      },
    },
  ];

  return (
    <Table<IUserResFind>
      columns={columns}
      fetchQueryProps={{ ...sortQuery, ...roleQueryForVariant(variant) }}
      service={usersService}
      onLoad={setTableProps}
    />
  );
};

